/**
 * ClaudeDocs — reference sections (tools → troubleshooting).
 *
 * Extracted verbatim from ../ClaudeDocs.tsx. No styling/logic changes.
 */

import React from 'react';

import { CodeBlock, Section } from './components';

export const ReferenceSections: React.FC = () => (
  <>
    <Section id="tools" title="MCP tool reference">
      <p>
        BuzzBit X exposes 100+ tools. Read tools are safe to call freely; write tools
        create drafts; execute tools (send / publish) go through a 30-second undo window.
        The complete live registry per workspace is always at{' '}
        <code className="px-1 bg-gray-100 rounded">bbx tools list</code>.
      </p>
      <ul className="list-disc pl-5 space-y-1 text-sm">
        <li>
          <strong>Analytics:</strong> <code>get_dashboard_overview</code>,{' '}
          <code>get_revenue_stats</code>, <code>get_growth_metrics</code>,{' '}
          <code>list_saved_reports</code>, <code>list_receipts</code>
        </li>
        <li>
          <strong>Customers:</strong> <code>list_customers</code>,{' '}
          <code>get_customer_details</code>, <code>search_customers</code>,{' '}
          <code>tag_customers</code>, <code>bulk_tag_customers</code>,{' '}
          <code>list_vip_tiers</code>, <code>create_vip_tier</code>,{' '}
          <code>list_carts</code>, <code>list_customer_events</code>,{' '}
          <code>list_browse_events</code>, <code>list_stock_notify_subscribers</code>
        </li>
        <li>
          <strong>Orders &amp; shipping:</strong> <code>list_orders</code>,{' '}
          <code>get_order_details</code>, <code>list_shipments</code>
        </li>
        <li>
          <strong>Products:</strong> <code>list_products</code>,{' '}
          <code>get_product_details</code>, <code>get_product_performance</code>
        </li>
        <li>
          <strong>Flows:</strong> <code>list_flows</code>, <code>get_flow_performance</code>,{' '}
          <code>create_flow_draft</code>, <code>update_flow_draft</code>,{' '}
          <code>activate_flow</code>, <code>cancel_pending_action</code>,{' '}
          <code>list_chat_flows</code>, <code>get_chat_flow</code>
        </li>
        <li>
          <strong>Campaigns &amp; templates:</strong> <code>list_campaigns</code>,{' '}
          <code>get_campaign_metrics</code>, <code>create_campaign_draft</code>,{' '}
          <code>create_campaign_draft_with_html</code>, <code>update_campaign_draft</code>,{' '}
          <code>validate_email_html</code>, <code>send_campaign</code>,{' '}
          <code>bulk_create_email_campaigns</code>, <code>list_email_templates</code>,{' '}
          <code>get_email_template</code>, <code>create_email_template</code>
        </li>
        <li>
          <strong>Social:</strong> <code>list_social_posts</code>,{' '}
          <code>get_social_performance</code>, <code>create_social_post_draft</code>,{' '}
          <code>update_social_post_draft</code>, <code>bulk_create_social_posts</code>,{' '}
          <code>publish_social_post</code>,{' '}
          <code>list_instagram_automation_rules</code>
        </li>
        <li>
          <strong>Inbox / DMs / WhatsApp:</strong> <code>list_conversations</code>,{' '}
          <code>get_conversation_messages</code>, <code>list_dm_templates</code>,{' '}
          <code>create_dm_draft</code>, <code>create_dm_template</code>,{' '}
          <code>send_dm_template</code>, <code>list_auto_reply_rules</code>
        </li>
        <li>
          <strong>Broadcasts:</strong> <code>list_broadcasts</code>,{' '}
          <code>create_broadcast_draft</code> (WhatsApp / Messenger / Instagram mass)
        </li>
        <li>
          <strong>Popups:</strong> <code>list_popups</code>,{' '}
          <code>get_popup_performance</code>, <code>create_popup_draft</code>,{' '}
          <code>update_popup_draft</code>
        </li>
        <li>
          <strong>Segments:</strong> <code>list_segments</code>, <code>create_segment</code>,{' '}
          <code>update_segment</code>
        </li>
        <li>
          <strong>Discounts:</strong> <code>create_discount_code</code>
        </li>
        <li>
          <strong>Media:</strong> <code>upload_media</code> (image ≤8MB, video ≤100MB)
        </li>
        <li>
          <strong>Drafts:</strong> <code>delete_draft</code> (24h soft-delete)
        </li>
        <li>
          <strong>Content:</strong> <code>list_blog_posts</code>,{' '}
          <code>create_blog_post_draft</code>
        </li>
        <li>
          <strong>Support:</strong> <code>list_support_tickets</code>,{' '}
          <code>get_support_ticket</code>, <code>reply_support_ticket</code>,{' '}
          <code>list_kb_articles</code>
        </li>
        <li>
          <strong>Boards / CRM:</strong> <code>list_boards</code>, <code>get_board</code>,{' '}
          <code>list_board_items</code>, <code>create_board_item</code>,{' '}
          <code>update_board_item_cell</code>
        </li>
        <li>
          <strong>Workspace:</strong> <code>get_workspace_brand</code>,{' '}
          <code>update_workspace_brand</code>, <code>get_workspace_limits</code>,{' '}
          <code>get_agent_settings</code>
        </li>
        <li>
          <strong>DMs (write/execute):</strong> <code>create_dm_draft</code>,{' '}
          <code>create_dm_template</code>, <code>send_dm_template</code>
        </li>
        <li>
          <strong>Team / RBAC:</strong> <code>list_team_members</code>,{' '}
          <code>invite_team_member</code>, <code>update_member_role</code>,{' '}
          <code>remove_team_member</code>, <code>list_roles_and_permissions</code>
        </li>
        <li>
          <strong>Billing (read-only):</strong> <code>get_subscription_status</code>,{' '}
          <code>list_invoices</code>, <code>get_payment_history</code> — Cardcom tokens
          are never returned
        </li>
        <li>
          <strong>Integrations:</strong> <code>list_integrations</code>,{' '}
          <code>get_integration_status</code>, <code>get_connection_health</code> —
          OAuth tokens are never returned
        </li>
        <li>
          <strong>Webhooks:</strong> <code>list_webhooks</code>, <code>create_webhook</code>,{' '}
          <code>update_webhook</code>, <code>delete_webhook</code> (30s undo). Signing
          secret returned once on creation
        </li>
        <li>
          <strong>Boards expansion:</strong> <code>list_board_views</code>,{' '}
          <code>list_board_widgets</code>, <code>list_board_automations</code>,{' '}
          <code>create_board_automation</code>, <code>delete_board_item</code> (30s undo)
        </li>
      </ul>
      <p>
        For the live registry (with descriptions and input schemas) at any time:{' '}
        <code className="px-1 bg-gray-100 rounded">bbx tools list</code> or{' '}
        <code className="px-1 bg-gray-100 rounded">bbx tools describe &lt;name&gt;</code>.
      </p>
    </Section>

    <Section id="gating" title="Per-merchant gating">
      <p>
        Settings → Integrations → Claude card → <strong>Tools</strong> tab. Toggle individual
        tools or whole categories on / off. Disabled tools return{' '}
        <code className="px-1 bg-gray-100 rounded">TOOL_DISABLED</code> to Claude before they
        run — no quota is charged for refused calls.
      </p>
      <p>
        Toggling also works via API:{' '}
        <code className="px-1 bg-gray-100 rounded">
          PATCH /api/integrations/claude/tools/&lt;name&gt; {'{ enabled: false }'}
        </code>
        .
      </p>
    </Section>

    <Section id="aliases" title="Aliases — custom names">
      <p>
        You can rename any canonical tool per-workspace. Useful when your team uses a
        different vocabulary.
      </p>
      <CodeBlock>{`bbx aliases create --canonical=list_customers --alias=list_my_customers
bbx aliases list
bbx aliases delete <id>`}</CodeBlock>
      <p>
        Aliases must match <code className="px-1 bg-gray-100 rounded">^[a-zA-Z0-9_-]{'{1,64}'}$</code>{' '}
        — that&apos;s an MCP spec limit, not ours. Non-ASCII alias <em>names</em> are not
        accepted; what you can customize is the <em>phrase</em> the user types in their
        prompt (e.g. teach Claude that &ldquo;my customer list&rdquo; means{' '}
        <code>list_my_customers</code> via a skill, then alias{' '}
        <code>list_customers → list_my_customers</code>).
      </p>
    </Section>

    <Section id="skills" title="Custom skills (Agency tier)">
      <p>
        Agency and Enterprise plans can upload markdown skill files that auto-load into the
        merchant&apos;s Claude session. Use them for client-specific playbooks (&ldquo;how we
        talk to VIPs&rdquo;, &ldquo;always check inventory before promos&rdquo;).
      </p>
      <CodeBlock>{`bbx skills upload --name=vip-playbook --file=./playbook.md --triggers="vip,whales,top customers"
bbx skills list`}</CodeBlock>
      <p>
        Skills are sandboxed to one workspace, max 64 KB each, and surface via the plugin to
        connected Claude clients automatically.
      </p>
    </Section>

    <Section id="quotas" title="Quotas & rate limits">
      <p>Quotas reset on your subscription billing period (or rolling 30 days if absent).</p>
      <div className="overflow-x-auto">
        <table className="text-sm w-full border border-gray-200 rounded">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-3 py-2">Tier</th>
              <th className="px-3 py-2">API calls</th>
              <th className="px-3 py-2">Drafts</th>
              <th className="px-3 py-2">Sends</th>
              <th className="px-3 py-2">Social</th>
              <th className="px-3 py-2">DMs</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            <tr>
              <td className="px-3 py-2 font-medium">Growth</td>
              <td className="px-3 py-2">5,000</td>
              <td className="px-3 py-2">100</td>
              <td className="px-3 py-2">10</td>
              <td className="px-3 py-2">30</td>
              <td className="px-3 py-2">100</td>
            </tr>
            <tr>
              <td className="px-3 py-2 font-medium">Scale</td>
              <td className="px-3 py-2">25,000</td>
              <td className="px-3 py-2">500</td>
              <td className="px-3 py-2">50</td>
              <td className="px-3 py-2">200</td>
              <td className="px-3 py-2">1,000</td>
            </tr>
            <tr>
              <td className="px-3 py-2 font-medium">Agency</td>
              <td className="px-3 py-2">100,000</td>
              <td className="px-3 py-2">unlimited</td>
              <td className="px-3 py-2">500</td>
              <td className="px-3 py-2">1,000</td>
              <td className="px-3 py-2">10,000</td>
            </tr>
            <tr>
              <td className="px-3 py-2 font-medium">Enterprise</td>
              <td className="px-3 py-2" colSpan={5}>
                Custom contract
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        Live usage: <code className="px-1 bg-gray-100 rounded">bbx metrics limits</code>,{' '}
        <code className="px-1 bg-gray-100 rounded">/buzzbit-quota</code>, or the{' '}
        <strong>Usage</strong> tab on the Claude card.
      </p>
    </Section>

    <Section id="safety" title="Safety & undo window">
      <ul className="list-disc pl-5 space-y-1">
        <li>
          <strong>Workspace isolation</strong> on every Prisma query — keys can&apos;t cross
          workspace boundaries.
        </li>
        <li>
          <strong>30-second undo window</strong> on <code>send_campaign</code>,{' '}
          <code>publish_social_post</code>, and <code>send_dm_template</code>. Abort with{' '}
          <code className="px-1 bg-gray-100 rounded">bbx flows cancel &lt;actionId&gt;</code>.
        </li>
        <li>
          <strong>Audit log:</strong> every tool call writes a SystemLog row with workspace,
          key, tool name, duration, and outcome — visible in the Activity tab.
        </li>
        <li>
          <strong>Rate limit:</strong> tier-based per-workspace bucket (Growth 30/min, Scale
          60/min, Agency 120/min, Enterprise 240/min).
        </li>
        <li>
          <strong>Idempotency:</strong> pass{' '}
          <code className="px-1 bg-gray-100 rounded">X-Idempotency-Key</code> on write calls
          to dedupe within 24h.
        </li>
      </ul>
    </Section>

    <Section id="troubleshooting" title="Troubleshooting">
      <dl className="space-y-3">
        <div>
          <dt className="font-medium text-gray-900">401 Unauthorized</dt>
          <dd className="text-sm">
            Key revoked or expired. Generate a new one in Settings → Integrations → Claude →
            Keys.
          </dd>
        </div>
        <div>
          <dt className="font-medium text-gray-900">429 Rate limit exceeded</dt>
          <dd className="text-sm">
            You hit the per-minute bucket. Wait a moment or upgrade. CLI: retry; the next
            window unblocks automatically.
          </dd>
        </div>
        <div>
          <dt className="font-medium text-gray-900">QUOTA_EXCEEDED</dt>
          <dd className="text-sm">
            A monthly counter capped out. Check <code>bbx metrics limits</code>. Either wait
            for the next period or upgrade.
          </dd>
        </div>
        <div>
          <dt className="font-medium text-gray-900">TOOL_DISABLED</dt>
          <dd className="text-sm">
            A merchant turned this tool off for the workspace. Enable it in the{' '}
            <strong>Tools</strong> tab of the Claude card.
          </dd>
        </div>
        <div>
          <dt className="font-medium text-gray-900">A custom tool name isn&apos;t routing</dt>
          <dd className="text-sm">
            The MCP spec only allows ASCII-safe tool names. Use a skill to teach Claude the
            phrase, then alias the canonical tool name.
          </dd>
        </div>
      </dl>
    </Section>
  </>
);
